import {
  DashboardConfigDTO,
  DashboardConfigDTOOutliersDisplayModeEnum,
  DashboardConfigDTOSmoothingAlgorithmEnum,
  DashboardConfigDTOSmoothingWindowModeEnum,
} from 'generated/leaderboard-client';

// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries -- TODO(strict-boundaries): legacy violation, remove after refactor
import { OutliersDisplayMode } from '@neptune/charts-domain';
import {
  SecondaryStyling,
  SecondaryStylingModelConverter,
} from '@neptune/shared/secondary-styling-domain';
import { SmoothingAlgorithm } from '@neptune/shared/smoothing-domain';
import { AxisScale, XAxisMode } from '@neptune/shared/widgets-domain';

import { getDashboardLockEnabled } from 'common/featureFlag';
import { AttributeDefinitionConverter } from 'domain/experiment/attribute';
import { ChartGlobalConfig, MetricDistinction } from 'domain/widget';

export type DashboardConfig = {
  chartGlobalConfig?: ChartGlobalConfig;
  secondaryStyling?: SecondaryStyling;
  locked?: boolean;
};

function smoothingAlgorithmFromApiToDomain(
  smoothingWindowMode: DashboardConfigDTOSmoothingAlgorithmEnum | undefined,
): SmoothingAlgorithm | undefined {
  switch (smoothingWindowMode) {
    case DashboardConfigDTOSmoothingAlgorithmEnum.EmaCausal:
      return 'emaCausal';
    case DashboardConfigDTOSmoothingAlgorithmEnum.GaussianCentered:
      return 'gaussianCentered';
    case DashboardConfigDTOSmoothingAlgorithmEnum.SmaCausal:
      return 'smaCausal';
    case DashboardConfigDTOSmoothingAlgorithmEnum.SmaCentered:
      return 'smaCentered';
  }
}

function smoothingAlgorithmFromDomainToApi(
  smoothingAlgorithm: SmoothingAlgorithm | undefined,
): DashboardConfigDTOSmoothingAlgorithmEnum | undefined {
  switch (smoothingAlgorithm) {
    case 'emaCausal':
      return DashboardConfigDTOSmoothingAlgorithmEnum.EmaCausal;
    case 'gaussianCentered':
      return DashboardConfigDTOSmoothingAlgorithmEnum.GaussianCentered;
    case 'smaCausal':
      return DashboardConfigDTOSmoothingAlgorithmEnum.SmaCausal;
    case 'smaCentered':
      return DashboardConfigDTOSmoothingAlgorithmEnum.SmaCentered;
  }
}

function smoothingAlgorithmFromSmoothingWindowMode(
  smoothingWindowMode: DashboardConfigDTOSmoothingWindowModeEnum | undefined,
): SmoothingAlgorithm | undefined {
  switch (smoothingWindowMode) {
    case DashboardConfigDTOSmoothingWindowModeEnum.Ema:
      return 'emaCausal';
    case DashboardConfigDTOSmoothingWindowModeEnum.Gaussian:
      return 'gaussianCentered';
    case DashboardConfigDTOSmoothingWindowModeEnum.Causal:
      return 'smaCausal';
    case DashboardConfigDTOSmoothingWindowModeEnum.Centered:
      return 'smaCentered';
  }
}

function rollbackSafeSmoothingWindowModeFromSmoothingAlgorithm(
  smoothingAlgorithm: SmoothingAlgorithm | undefined,
):
  | typeof DashboardConfigDTOSmoothingWindowModeEnum.Causal
  | typeof DashboardConfigDTOSmoothingWindowModeEnum.Centered
  | undefined {
  switch (smoothingAlgorithm) {
    case 'emaCausal':
    case 'smaCausal':
      return DashboardConfigDTOSmoothingWindowModeEnum.Causal;
    case 'gaussianCentered':
    case 'smaCentered':
      return DashboardConfigDTOSmoothingWindowModeEnum.Centered;
  }
}

function outliersDisplayModeFromApiToDomain(
  outliersDisplayMode: DashboardConfigDTOOutliersDisplayModeEnum | undefined,
): OutliersDisplayMode | undefined {
  switch (outliersDisplayMode) {
    case DashboardConfigDTOOutliersDisplayModeEnum.None:
      return 'none';
    case DashboardConfigDTOOutliersDisplayModeEnum.Outline:
      return 'outline';
  }
}

function outliersDisplayModeFromDomainToApi(
  outliersDisplayMode: OutliersDisplayMode | undefined,
): DashboardConfigDTOOutliersDisplayModeEnum | undefined {
  switch (outliersDisplayMode) {
    case 'none':
      return DashboardConfigDTOOutliersDisplayModeEnum.None;
    case 'outline':
      return DashboardConfigDTOOutliersDisplayModeEnum.Outline;
  }
}

export const createDashboardConfigModelConverter = () => {
  function fromApiToDomain(config: DashboardConfigDTO | undefined): DashboardConfig | undefined {
    if (!config) {
      return undefined;
    }

    const {
      metricsStepsRange,
      xaxisRange,
      xaxisScale: xAxisScale,
      yaxisScale: yAxisScale,
      xaxisMode: xAxisMode,
      xaxisMetric: xAxisMetric,
      smoothing,
      showMetricBy,
      showPreviews,
      showCompletion,
      smoothingWindowMode,
      smoothingAlgorithm,
      showOriginalLineOnSmoothing,
      outliersDisplayMode,
      secondaryStyling,
      locked,
    } = config;

    return {
      locked: getDashboardLockEnabled() ? locked : undefined,
      chartGlobalConfig: {
        metricsStepsRange: metricsStepsRange
          ? [metricsStepsRange.from, metricsStepsRange.to]
          : undefined,
        smoothing,
        xAxisMode: xAxisMode as XAxisMode,
        xAxisMetric:
          xAxisMetric &&
          AttributeDefinitionConverter.attributeDefinitionFromApiToDomain(xAxisMetric),
        xAxisScale: xAxisScale as AxisScale,
        yAxisScale: yAxisScale as AxisScale,
        xAxisRange: xaxisRange ? [xaxisRange.from, xaxisRange.to] : undefined,
        showMetricBy: showMetricBy ?? MetricDistinction.LineStyle,
        showCompletion: showCompletion ?? showPreviews,
        smoothingAlgorithm:
          smoothingAlgorithmFromApiToDomain(smoothingAlgorithm) ??
          smoothingAlgorithmFromSmoothingWindowMode(smoothingWindowMode),
        showOriginalLineOnSmoothing,
        outliersDisplayMode: outliersDisplayModeFromApiToDomain(outliersDisplayMode),
      },
      secondaryStyling: SecondaryStylingModelConverter.fromApiToDomain(secondaryStyling),
    };
  }

  function fromDomainToApi(config: DashboardConfig | undefined): DashboardConfigDTO | undefined {
    if (!config) {
      return undefined;
    }

    const { chartGlobalConfig, secondaryStyling, locked } = config;

    const {
      xAxisScale,
      yAxisScale,
      xAxisMode,
      xAxisMetric,
      xAxisRange,
      metricsStepsRange,
      smoothing,
      showMetricBy,
      showCompletion,
      smoothingAlgorithm,
      showOriginalLineOnSmoothing,
      outliersDisplayMode,
    } = chartGlobalConfig ?? {};

    return {
      locked: getDashboardLockEnabled() ? locked : undefined,
      metricsStepsRange: {
        from: metricsStepsRange?.[0],
        to: metricsStepsRange?.[1],
      },
      xaxisRange: {
        from: xAxisRange?.[0],
        to: xAxisRange?.[1],
      },
      xaxisScale: xAxisScale,
      yaxisScale: yAxisScale,
      xaxisMode: xAxisMode,
      xaxisMetric:
        xAxisMetric && AttributeDefinitionConverter.attributeDefinitionFromDomainToApi(xAxisMetric),
      smoothing,
      showMetricBy,
      showCompletion,
      /** @deprecated use showCompletion instead, we keep it to make rollback easy */
      showPreviews: showCompletion,
      smoothingWindowMode:
        rollbackSafeSmoothingWindowModeFromSmoothingAlgorithm(smoothingAlgorithm),
      smoothingAlgorithm: smoothingAlgorithmFromDomainToApi(smoothingAlgorithm),
      showOriginalLineOnSmoothing,
      outliersDisplayMode: outliersDisplayModeFromDomainToApi(outliersDisplayMode),
      secondaryStyling: SecondaryStylingModelConverter.fromDomainToApi(secondaryStyling),
    };
  }

  return {
    fromDomainToApi,
    fromApiToDomain,
  };
};

export const DashboardConfigModelConverter = createDashboardConfigModelConverter();
